// The guard clocks this client holds (R2080, ui-map-interface.md §6.3.6).
//
// A GUARD takes one hostile round out of the water on a cooldown
// (numbers.md §5). The server sends a guard's clock to the client that may
// read it and to no other: an own set Sentinel with the Refit node rides
// the own fleet frame (`FleetState.guard`), and an arcade Reef Breakwater
// of the seat's team or a public one rides the Empire frame. A hostile
// guard has no record here, so the map draws its kill alone and no mark of
// its clock (the owner's ruling of 2026-10-13).
//
// The wire states the time left in whole map seconds and re-sends it only
// at a change of its bucket, so this tracker counts it down between two
// frames at the tactical scale, the way `ScanSeen` counts a scan reveal. A
// new end is taken only when the figure CHANGES.
//
// The guard layer (`fast/layers/guard.ts`) writes the live phase and the
// count of hostile rounds in reach onto each record every frame, so the
// readout chip of a card states what the map draws.

import type { GuardPhase } from './render/guard-look';

/** One guard whose clock this client holds. */
export interface GuardClock {
  /** The host: a hull (`fleet`) or an arcade structure (`site`). */
  kind: 'fleet' | 'site';
  /** The fleet id or the site id. */
  id: number;
  /** The radius of the water the guard keeps, world px. */
  reach: number;
  /** The reload period, map seconds. */
  every: number;
  /** The guard's side; 0 is a public structure, which takes every round. */
  faction: number;
  /** The figure the wire stated last, and the wall ms its clock ends. */
  sec: number;
  endMs: number;
  /** What the guard layer drew on the last frame. */
  phase: GuardPhase;
  near: number;
}

/** What `observeFleets` reads of one fleet of a State frame. */
export interface GuardFleetWire {
  id: number;
  faction: number;
  guard?: { reachPx: number; everySec: number; leftSec: number };
}

/** The guard clocks of this client, by host. */
export class GuardClocks {
  private readonly fleets = new Map<number, GuardClock>();
  private readonly sites = new Map<number, GuardClock>();
  /** Map seconds per wall second, at the last frame. */
  private scale = 1;

  /**
   * One State frame: `fleets` the hulls it carries, `removed` the ids it
   * drops, `full` whether it replaces the picture, `mapPerWall` the
   * tactical scale. A hull the frame carries with no guard is no guard now.
   */
  observeFleets(fleets: readonly GuardFleetWire[], removed: readonly number[], full: boolean,
    nowMs: number, mapPerWall: number): void {
    this.scale = mapPerWall > 0 ? mapPerWall : 1;
    if (full) this.fleets.clear();
    for (const id of removed) this.fleets.delete(id);
    for (const f of fleets) {
      const g = f.guard;
      if (!g || !(g.reachPx > 0) || !(g.everySec > 0)) {
        this.fleets.delete(f.id);
        continue;
      }
      this.put(this.fleets, 'fleet', f.id, g.reachPx, g.everySec, f.faction, g.leftSec, nowMs);
    }
  }

  /**
   * The guard structures of an arcade match whose clock this client reads
   * (R2085): the hello states each one's reach and period, and the Empire
   * frame the time left of the ones the seat may read. A structure the
   * list leaves out has no record.
   */
  observeSites(list: readonly { siteId: number; reach: number; every: number; faction: number; leftSec: number }[],
    nowMs: number, mapPerWall: number): void {
    this.scale = mapPerWall > 0 ? mapPerWall : 1;
    const keep = new Set<number>();
    for (const s of list) {
      if (!(s.reach > 0) || !(s.every > 0)) continue;
      keep.add(s.siteId);
      this.put(this.sites, 'site', s.siteId, s.reach, s.every, s.faction, s.leftSec, nowMs);
    }
    // A Map iterates safely while its current entry is deleted.
    for (const id of this.sites.keys()) if (!keep.has(id)) this.sites.delete(id);
  }

  private put(into: Map<number, GuardClock>, kind: GuardClock['kind'], id: number,
    reach: number, every: number, faction: number, sec: number, nowMs: number): void {
    const had = into.get(id);
    if (had) {
      had.reach = reach; had.every = every; had.faction = faction;
      if (had.sec === sec) return;
      had.sec = sec;
      had.endMs = nowMs + sec / this.scale * 1000;
      return;
    }
    into.set(id, {
      kind, id, reach, every, faction, sec, endMs: nowMs + sec / this.scale * 1000,
      phase: 'idle', near: 0,
    });
  }

  /** Map seconds left on a guard's clock; 0 when it is ready. */
  leftSec(g: GuardClock, nowMs: number): number {
    return Math.max(0, (g.endMs - nowMs) / 1000 * this.scale);
  }

  /** The share of the clock left, 0 (ready) to 1. */
  frac(g: GuardClock, nowMs: number): number {
    return g.every > 0 ? Math.min(1, this.leftSec(g, nowMs) / g.every) : 0;
  }

  /** The guard of an own hull, if this client holds its clock. */
  fleet(id: number): GuardClock | undefined { return this.fleets.get(id); }

  /** The guard of a structure, if this client holds its clock. */
  site(id: number): GuardClock | undefined { return this.sites.get(id); }

  /** Every guard this client holds a clock for: the hulls, then the sites.
   *  It walks the maps with their own `forEach`, so a frame allocates no
   *  iterator (R131). */
  forEach(fn: (g: GuardClock) => void): void {
    this.fleets.forEach(fn);
    this.sites.forEach(fn);
  }

  /** How many guards this client holds a clock for. */
  get size(): number { return this.fleets.size + this.sites.size; }

  /** Forgets every clock: a new hello. */
  clear(): void {
    this.fleets.clear();
    this.sites.clear();
  }
}
